// src/pages/admin/AdminModelManagement.jsx

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { matchingModelAPI } from '../../services/api';
import { Spinner } from '../../components/shared/UI';
import toast from 'react-hot-toast';
import { Save, RotateCcw } from 'lucide-react';

import { AdminCircumstancesScorer } from './AdminCircumstancesScorer';
import { AdminPreferencesScorer }   from './AdminPreferencesScorer';
import { AdminAbilitiesScorer }     from './AdminAbilitiesScorer';
import { AdminRawSkillsScorer }     from './AdminRawSkillsScorer';
import { AdminEducationScorer }     from './AdminEducationScorer';
import { AdminExperienceScorer }    from './AdminExperienceScorer';
import { AdminLanguageScorer }      from './AdminLanguageScorer';
import { AdminAttributesScorer }    from './AdminAttributesScorer';

const TABS = [
  { key: 'config',        label: 'Configuration' },
  { key: 'circumstances', label: 'Circumstances' },
  { key: 'preferences',   label: 'Seeker Preferences' },
  { key: 'abilities',     label: 'Abilities' },
  { key: 'raw-skills',    label: 'Raw Skills' },
  { key: 'education',     label: 'Education' },
  { key: 'experience',    label: 'Experience' },
  { key: 'language',      label: 'Language' },
  { key: 'attributes',    label: 'Attributes' },
];

const TAB_COMPONENTS = {
  circumstances: AdminCircumstancesScorer,
  preferences:   AdminPreferencesScorer,
  abilities:     AdminAbilitiesScorer,
  'raw-skills':  AdminRawSkillsScorer,
  education:     AdminEducationScorer,
  experience:    AdminExperienceScorer,
  language:      AdminLanguageScorer,
  attributes:    AdminAttributesScorer,
};

/* ── Weight defaults ─────────────────────────────────────── */
const WEIGHT_DEFAULTS = {
  modelName: '',
  modelVersion: '',
  scorers: {
    circumstances: { weight: 10, internal: { availabilityWeight: 40, salaryWeight: 20 } },
    preferences:   { weight: 10 },
    skillsAbilities: {
      weight: 80,
      sub: {
        abilities:  { weight: 20 },
        rawSkills:  { weight: 25 },
        education:  { weight: 15 },
        experience: { weight: 20 },
        language:   { weight: 10 },
        attributes: { weight: 10 },
      },
    },
  },
};

const SUB_SCORER_INFO = [
  { key: 'abilities',  label: 'Abilities' },
  { key: 'rawSkills',  label: 'Raw Skills' },
  { key: 'education',  label: 'Education' },
  { key: 'experience', label: 'Experience' },
  { key: 'language',   label: 'Language' },
  { key: 'attributes', label: 'Attributes' },
];

/* ── Weight row ──────────────────────────────────────────── */
const WeightRow = ({ label, value, onChange, indent, bold }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '10px 0', paddingLeft: indent ? 24 : 0 }}>
    <label className="form-label" style={{ marginBottom: 0, fontWeight: bold ? 700 : undefined, fontSize: indent ? 12 : undefined, color: indent ? '#666' : undefined }}>{label}</label>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <input
        type="number" min="0" max="100" value={value}
        onChange={e => onChange(Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))}
        className="form-input"
        style={{ width: 80, textAlign: 'center', fontFamily: 'monospace', fontWeight: 700 }}
      />
      <span style={{ fontSize: 12, color: '#888' }}>/ 100</span>
    </div>
  </div>
);

/* ── Configuration tab ───────────────────────────────────── */
const ConfigurationTab = ({ modelId }) => {
  const [config, setConfig] = useState(WEIGHT_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const mergeConfig = (d) => ({
    modelName: d.modelName || '',
    modelVersion: d.modelVersion || '',
    scorers: {
      circumstances: {
        ...WEIGHT_DEFAULTS.scorers.circumstances, ...d.scorers?.circumstances,
        internal: { ...WEIGHT_DEFAULTS.scorers.circumstances.internal, ...d.scorers?.circumstances?.internal },
      },
      preferences:   { ...WEIGHT_DEFAULTS.scorers.preferences, ...d.scorers?.preferences },
      skillsAbilities: {
        ...WEIGHT_DEFAULTS.scorers.skillsAbilities,
        ...d.scorers?.skillsAbilities,
        sub: {
          abilities:  { ...WEIGHT_DEFAULTS.scorers.skillsAbilities.sub.abilities,  ...d.scorers?.skillsAbilities?.sub?.abilities },
          rawSkills:  { ...WEIGHT_DEFAULTS.scorers.skillsAbilities.sub.rawSkills,  ...d.scorers?.skillsAbilities?.sub?.rawSkills },
          education:  { ...WEIGHT_DEFAULTS.scorers.skillsAbilities.sub.education,  ...d.scorers?.skillsAbilities?.sub?.education },
          experience: { ...WEIGHT_DEFAULTS.scorers.skillsAbilities.sub.experience, ...d.scorers?.skillsAbilities?.sub?.experience },
          language:   { ...WEIGHT_DEFAULTS.scorers.skillsAbilities.sub.language,   ...d.scorers?.skillsAbilities?.sub?.language },
          attributes: { ...WEIGHT_DEFAULTS.scorers.skillsAbilities.sub.attributes, ...d.scorers?.skillsAbilities?.sub?.attributes },
        },
      },
    },
  });

  useEffect(() => {
    if (!modelId) { setLoading(false); return; }
    matchingModelAPI.getModel(modelId)
      .then(({ data }) => { if (data.data) setConfig(mergeConfig(data.data)); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [modelId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await matchingModelAPI.updateModel(modelId, config);
      setConfig(mergeConfig(data.data));
      toast.success('Model configuration saved');
    } catch { /* interceptor toasts */ }
    finally { setSaving(false); }
  };

  const handleReset = () => {
    setConfig(WEIGHT_DEFAULTS);
    toast.success('Reset to defaults');
  };

  const updateScorer = (key, patch) =>
    setConfig(c => ({ ...c, scorers: { ...c.scorers, [key]: { ...c.scorers[key], ...patch } } }));

  const updateSA = (patch) =>
    setConfig(c => ({ ...c, scorers: { ...c.scorers, skillsAbilities: { ...c.scorers.skillsAbilities, ...patch } } }));

  const updateSub = (key, patch) =>
    setConfig(c => ({
      ...c,
      scorers: {
        ...c.scorers,
        skillsAbilities: {
          ...c.scorers.skillsAbilities,
          sub: { ...c.scorers.skillsAbilities.sub, [key]: { ...c.scorers.skillsAbilities.sub[key], ...patch } },
        },
      },
    }));

  const updateInternal = (patch) =>
    setConfig(c => ({
      ...c,
      scorers: {
        ...c.scorers,
        circumstances: {
          ...c.scorers.circumstances,
          internal: { ...c.scorers.circumstances.internal, ...patch },
        },
      },
    }));

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}><Spinner size={32} /></div>;

  const s = config.scorers;
  const topTotal = s.circumstances.weight + s.preferences.weight + s.skillsAbilities.weight;
  const subTotal = SUB_SCORER_INFO.reduce((sum, si) => sum + s.skillsAbilities.sub[si.key].weight, 0);

  return (
    <div>
      {/* ── Model Identity ── */}
      <div className="section-box" style={{ marginBottom: 20 }}>
        <div className="section-box-header">
          <span>Model Identity</span>
        </div>
        <div className="section-box-body" style={{ padding: 20 }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: 2, minWidth: 200 }}>
              <label className="form-label">Model Name</label>
              <input value={config.modelName} onChange={e => setConfig(c => ({ ...c, modelName: e.target.value }))}
                className="form-input" placeholder="e.g. Default Model" />
            </div>
            <div style={{ flex: 1, minWidth: 120 }}>
              <label className="form-label">Version</label>
              <input value={config.modelVersion} onChange={e => setConfig(c => ({ ...c, modelVersion: e.target.value }))}
                className="form-input" placeholder="e.g. 1.0" />
            </div>
          </div>
        </div>
      </div>

      {/* ── Scorer Weights ── */}
      <div className="section-box" style={{ marginBottom: 20 }}>
        <div className="section-box-header">
          <span>Scorer Weights</span>
        </div>
        <div className="section-box-body" style={{ padding: 20 }}>
          <WeightRow label="Circumstances" value={s.circumstances.weight} bold
            onChange={v => updateScorer('circumstances', { weight: v })} />
          <div style={{ borderTop: '1px solid #eee' }} />

          <WeightRow label="Seeker Preferences" value={s.preferences.weight} bold
            onChange={v => updateScorer('preferences', { weight: v })} />
          <div style={{ borderTop: '1px solid #eee' }} />

          <WeightRow label="Skills & Abilities" value={s.skillsAbilities.weight} bold
            onChange={v => updateSA({ weight: v })} />

          <div style={{ borderTop: '1px solid #ddd', marginTop: 8, paddingTop: 12,
            display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: topTotal === 100 ? '#28A745' : '#CC0000' }}>
              Total: {topTotal} / 100
            </span>
          </div>
          {topTotal !== 100 && (
            <div style={{ fontSize: 12, color: '#CC0000', textAlign: 'right', marginTop: 4 }}>
              Scorer weights should sum to 100. They will be normalised at scoring time.
            </div>
          )}
        </div>
      </div>

      {/* ── Sub-Scorer Weights (Skills & Abilities) ── */}
      <div className="section-box" style={{ marginBottom: 20 }}>
        <div className="section-box-header">
          <span>Skills & Abilities — Sub-Scorer Weights</span>
        </div>
        <div className="section-box-body" style={{ padding: 20 }}>
          {SUB_SCORER_INFO.map((si, i) => (
            <React.Fragment key={si.key}>
              {i > 0 && <div style={{ borderTop: '1px solid #eee' }} />}
              <WeightRow label={si.label} value={s.skillsAbilities.sub[si.key].weight}
                onChange={v => updateSub(si.key, { weight: v })} />
            </React.Fragment>
          ))}

          <div style={{ borderTop: '1px solid #ddd', marginTop: 8, paddingTop: 12,
            display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: subTotal === 100 ? '#28A745' : '#CC0000' }}>
              Total: {subTotal} / 100
            </span>
          </div>
          {subTotal !== 100 && (
            <div style={{ fontSize: 12, color: '#CC0000', textAlign: 'right', marginTop: 4 }}>
              Sub-scorer weights should sum to 100. They will be normalised at scoring time.
            </div>
          )}
        </div>
      </div>

      {/* ── Circumstances Internal Weights ── */}
      <div className="section-box" style={{ marginBottom: 20 }}>
        <div className="section-box-header">
          <span>Circumstances — Internal Weights</span>
        </div>
        <div className="section-box-body" style={{ padding: 20 }}>
          <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
            Controls how availability and salary contribute within the Circumstances scorer.
            Work rights is flag-only and does not contribute to the score.
          </p>

          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0',
            color: '#999', fontSize: 13, fontStyle: 'italic',
          }}>
            <span style={{ flex: 1 }}>Work Rights</span>
            <span style={{ fontSize: 12, background: '#f0f0f0', padding: '2px 10px', borderRadius: 10 }}>flag only</span>
          </div>
          <div style={{ borderTop: '1px solid #eee' }} />

          <WeightRow label="Availability" value={s.circumstances.internal.availabilityWeight}
            onChange={v => updateInternal({ availabilityWeight: v })} />
          <div style={{ borderTop: '1px solid #eee' }} />

          <WeightRow label="Salary" value={s.circumstances.internal.salaryWeight}
            onChange={v => updateInternal({ salaryWeight: v })} />

          <div style={{ borderTop: '1px solid #ddd', marginTop: 8, paddingTop: 12,
            display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: '#888' }}>
              These weights are normalised at scoring time (do not need to sum to 100).
            </span>
          </div>
        </div>
      </div>

      {/* ── Actions ── */}
      <div style={{ display: 'flex', gap: 12 }}>
        <button onClick={handleReset} className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <RotateCcw size={15} /><span>Reset Defaults</span>
        </button>
        <button onClick={handleSave} disabled={saving}
          className="btn btn-success" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          {saving ? <Spinner size={16} /> : <><Save size={15} /><span>Save Configuration</span></>}
        </button>
      </div>
    </div>
  );
};

/* ── Tab button style ────────────────────────────────────── */
const tabStyle = (active) => ({
  padding: '10px 16px', fontSize: 13, fontWeight: active ? 700 : 500,
  color: active ? '#0e4e95' : '#666', background: 'none', border: 'none',
  borderBottom: active ? '3px solid #0e4e95' : '3px solid transparent',
  cursor: 'pointer', fontFamily: "'Exo 2', sans-serif", whiteSpace: 'nowrap',
  marginBottom: -2, transition: 'all 0.15s',
});

/* ── Responsive tab bar ──────────────────────────────────── */
const ResponsiveTabBar = ({ tabs, activeTab, onTabChange }) => {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(tabs.length);
  const [dropOpen, setDropOpen] = useState(false);

  const measure = useCallback(() => {
    const container = containerRef.current;
    const hidden = measureRef.current;
    if (!container || !hidden) return;

    const containerWidth = container.offsetWidth;
    const buttons = hidden.querySelectorAll('[data-tab-measure]');
    if (!buttons.length) return;

    // Measure "Other" button width (approx 90px)
    const otherWidth = 90;
    let totalWidth = 0;
    let fitCount = 0;

    for (let i = 0; i < buttons.length; i++) {
      const w = buttons[i].offsetWidth;
      const needsOther = i < buttons.length - 1; // not the last — may need Other button
      const available = needsOther ? containerWidth - otherWidth : containerWidth;
      if (totalWidth + w <= available) {
        totalWidth += w;
        fitCount = i + 1;
      } else {
        break;
      }
    }

    // If all fit, show all; otherwise show fitCount visible + Other
    if (fitCount >= tabs.length) {
      setVisibleCount(tabs.length);
    } else {
      // Ensure at least 1 visible tab
      setVisibleCount(Math.max(1, fitCount));
    }
  }, [tabs.length]);

  useEffect(() => {
    measure();
    const ro = new ResizeObserver(measure);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [measure]);

  const visibleTabs = tabs.slice(0, visibleCount);
  const overflowTabs = tabs.slice(visibleCount);
  const activeInOverflow = overflowTabs.some(t => t.key === activeTab);

  return (
    <div ref={containerRef} style={{ position: 'relative', borderBottom: '2px solid #ddd', marginBottom: 20 }}>
      {/* Hidden measure container */}
      <div ref={measureRef} style={{ position: 'absolute', visibility: 'hidden', height: 0, overflow: 'hidden', display: 'flex', whiteSpace: 'nowrap' }}>
        {tabs.map(tab => (
          <span key={tab.key} data-tab-measure style={{ ...tabStyle(false), display: 'inline-block' }}>{tab.label}</span>
        ))}
      </div>

      {/* Visible tabs */}
      <div style={{ display: 'flex' }}>
        {visibleTabs.map(tab => (
          <button key={tab.key} onClick={() => { onTabChange(tab.key); setDropOpen(false); }}
            style={tabStyle(tab.key === activeTab)}>
            {tab.label}
          </button>
        ))}

        {overflowTabs.length > 0 && (
          <div style={{ position: 'relative' }}>
            <button onClick={() => setDropOpen(prev => !prev)}
              style={tabStyle(activeInOverflow)}>
              {activeInOverflow ? tabs.find(t => t.key === activeTab)?.label : 'Other'} ▾
            </button>
            {dropOpen && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 98 }} onClick={() => setDropOpen(false)} />
                <div style={{
                  position: 'absolute', top: '100%', right: 0, zIndex: 99,
                  background: '#fff', border: '1px solid #ddd', borderRadius: 6,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: 180, marginTop: 4,
                }}>
                  {overflowTabs.map(tab => (
                    <button key={tab.key}
                      onClick={() => { onTabChange(tab.key); setDropOpen(false); }}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        padding: '10px 16px', fontSize: 13, border: 'none', background: 'none',
                        cursor: 'pointer', fontFamily: "'Exo 2', sans-serif",
                        fontWeight: tab.key === activeTab ? 700 : 400,
                        color: tab.key === activeTab ? '#0e4e95' : '#333',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#f0f6ff'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
                      {tab.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Main page with tabs ─────────────────────────────────── */
export const AdminModelManagement = () => {
  const { modelId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'config';
  const [modelName, setModelName] = useState('');

  useEffect(() => {
    if (!modelId) return;
    matchingModelAPI.getModel(modelId)
      .then(({ data }) => setModelName(data.data?.modelName || 'Untitled Model'))
      .catch(() => navigate('/admin/matching/models', { replace: true }));
  }, [modelId]);

  const setTab = (key) => setSearchParams({ tab: key }, { replace: true });

  const TabContent = TAB_COMPONENTS[activeTab];

  return (
    <div className="page-content-wide">
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={() => navigate('/admin/matching/models')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fff', fontSize: 18, padding: 0, fontFamily: "'Exo 2', sans-serif" }}>←</button>
        <h1>{modelName || 'Model'}</h1>
      </div>

      {/* Tab bar */}
      <ResponsiveTabBar tabs={TABS} activeTab={activeTab} onTabChange={setTab} />

      {/* Tab content */}
      {activeTab === 'config'
        ? <ConfigurationTab modelId={modelId} />
        : TabContent
          ? <TabContent />
          : null
      }
    </div>
  );
};
